import { portfolio } from "../data/portfolio";
import { SectionHead } from "../components/SectionHead";

function ProjectArt({ idx }: { idx: number }) {
  const palette = [
    ["var(--md-sys-color-primary-container)", "var(--md-sys-color-primary)"],
    ["var(--md-sys-color-secondary-container)", "var(--md-sys-color-secondary)"],
    ["var(--md-sys-color-tertiary-container)", "var(--md-sys-color-tertiary)"],
  ][idx % 3];

  const variant = (["rings", "grid", "waves", "nodes"] as const)[idx % 4];

  if (variant === "rings") {
    return (
      <svg viewBox="0 0 400 225" preserveAspectRatio="xMidYMid slice">
        <rect width="400" height="225" fill={palette[0]} />
        {[10, 25, 45, 70, 100, 135].map((r, i) => (
          <circle
            key={i}
            cx="300"
            cy="112"
            r={r * 1.4}
            fill="none"
            stroke={palette[1]}
            strokeWidth="1.2"
            opacity={0.18 + i * 0.08}
          />
        ))}
        <circle cx="300" cy="112" r="8" fill={palette[1]} />
      </svg>
    );
  }
  if (variant === "grid") {
    return (
      <svg viewBox="0 0 400 225" preserveAspectRatio="xMidYMid slice">
        <rect width="400" height="225" fill={palette[0]} />
        {Array.from({ length: 9 }).map((_, r) =>
          Array.from({ length: 16 }).map((__, c) => (
            <rect
              key={r + "-" + c}
              x={c * 25 + 4}
              y={r * 25 + 4}
              width="18"
              height="18"
              rx="3"
              fill={palette[1]}
              opacity={((r * c + idx) % 7) / 18}
            />
          ))
        )}
      </svg>
    );
  }
  if (variant === "waves") {
    const path = (yOff: number) =>
      "M 0 " + yOff + " C 80 " + (yOff - 30) + " 160 " + (yOff + 30) + " 240 " + yOff + " S 400 " + (yOff - 30) + " 400 " + yOff;
    return (
      <svg viewBox="0 0 400 225" preserveAspectRatio="xMidYMid slice">
        <rect width="400" height="225" fill={palette[0]} />
        {[60, 100, 140, 180].map((y, i) => (
          <path key={i} d={path(y)} fill="none" stroke={palette[1]} strokeWidth="1.6" opacity={0.25 + i * 0.15} />
        ))}
      </svg>
    );
  }
  // nodes
  const nodes = Array.from({ length: 14 }).map((_, i) => ({
    x: 30 + ((i * 73) % 340),
    y: 30 + ((i * 47) % 160),
  }));
  return (
    <svg viewBox="0 0 400 225" preserveAspectRatio="xMidYMid slice">
      <rect width="400" height="225" fill={palette[0]} />
      {nodes.map((n, i) =>
        nodes.slice(i + 1, i + 3).map((m, j) => (
          <line
            key={i + "-" + j}
            x1={n.x}
            y1={n.y}
            x2={m.x}
            y2={m.y}
            stroke={palette[1]}
            strokeWidth="1"
            opacity="0.3"
          />
        ))
      )}
      {nodes.map((n, i) => (
        <circle key={i} cx={n.x} cy={n.y} r={i === 0 ? 8 : 5} fill={palette[1]} opacity={i === 0 ? 1 : 0.7} />
      ))}
    </svg>
  );
}

// Official Google Play badges. Two artworks — the light one is outlined with
// dark wordmark, the dark one is a filled black lockup — swapped by theme in
// CSS so the correct badge is already painted on first frame.
function PlayStoreBadge({ label }: { label: string }) {
  const base = import.meta.env.BASE_URL;
  return (
    <>
      <img
        className="md-store__badge md-store__badge--light"
        src={`${base}store/google-play-light.svg`}
        alt={label}
        width={133}
        height={40}
        loading="lazy"
        decoding="async"
      />
      <img
        className="md-store__badge md-store__badge--dark"
        src={`${base}store/google-play-dark.svg`}
        alt={label}
        width={135}
        height={40}
        loading="lazy"
        decoding="async"
      />
    </>
  );
}

export function Projects() {
  return (
    <section id="projects" className="md-section">
      <div className="md-container">
        <SectionHead
          num="03"
          eyebrow="Projects"
          title="Things I've shipped or am still shipping."
          dek="Production systems, research artifacts, and a couple of weekend builds I'm still proud of."
        />
        <div className="m3-projects__grid">
          {portfolio.projects.map((p, i) => (
            <article key={p.name} className="md-card md-card--elevated md-card--interactive m3-project reveal">
              <div className="m3-project__art">
                <ProjectArt idx={i} />
              </div>
              <div className="m3-project__body">
                <div className="md-label-l m3-project__kicker">{p.kicker}</div>
                <h3 className="md-title-l m3-project__title">{p.name}</h3>
                <p className="md-body-m m3-project__blurb">{p.blurb}</p>
              </div>
              <div className="m3-project__stack">
                {p.stack.map((s) => (
                  <span
                    key={s}
                    className="md-chip md-chip--assist"
                    style={{ height: 26, padding: "0 12px", fontSize: 12 }}
                  >
                    {s}
                  </span>
                ))}
              </div>
              <div className="m3-project__foot">
                <span className="md-label-m dim">{p.year}</span>
                <div className="m3-project__actions">
                  {p.playStore && (
                    <a
                      className="md-store"
                      href={p.playStore}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Get ${p.name} on Google Play`}
                    >
                      <PlayStoreBadge label={`Get ${p.name} on Google Play`} />
                    </a>
                  )}
                  {p.href && (
                    <a
                      className="md-btn md-btn--filled"
                      href={p.href}
                      target="_blank"
                      rel="noreferrer"
                      style={{ height: 36, padding: "0 16px" }}
                    >
                      View
                      <span className="material-symbols-rounded">arrow_outward</span>
                    </a>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
